import { useRef, useCallback, useState } from "react";
import { Link } from "lucide-react";
import ProfileImageModal from "./ProfileImageModal";

const ResponsiveProfileImage = ({ imageUrls, alt, className }) => {
  const getImageUrl = (size) => {
    if (!imageUrls) return null;
    if (typeof imageUrls === "string") return imageUrls;
    return imageUrls[size] || imageUrls.medium || imageUrls.large;
  };

  const thumbnailUrl = getImageUrl("thumbnail");
  const smallUrl = getImageUrl("small");
  const mediumUrl = getImageUrl("medium");
  const largeUrl = getImageUrl("large");

  return (
    <picture>
      <source
        media="(max-width: 640px)"
        type="image/avif"
        srcSet={`${smallUrl || mediumUrl} 1x, ${mediumUrl || largeUrl} 2x`}
      />
      <source
        media="(max-width: 1024px)"
        type="image/avif"
        srcSet={`${mediumUrl || largeUrl} 1x, ${largeUrl} 2x`}
      />
      <source
        media="(min-width: 1025px)"
        type="image/avif"
        srcSet={`${largeUrl} 1x`}
      />
      <img
        src={mediumUrl || largeUrl || smallUrl || thumbnailUrl}
        alt={alt}
        className={className}
        loading="lazy"
        decoding="async"
      />
    </picture>
  );
};

export default function ProfileSection({
  userData,
  previewImage,
  onImageUpload,
  isUploadingImage,
  onRemoveImage,
  isRemovingImage,
}) {
  const fileInputRef = useRef(null);
  const [showModal, setShowModal] = useState(false);

  const handlePlusClick = useCallback(() => {
    setShowModal(true);
  }, []);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e) => {
      onImageUpload(e);
      // setShowModal(false);
    },
    [onImageUpload],
  );

  const handleRemoveClick = useCallback(async () => {
    if (onRemoveImage) {
      await onRemoveImage();
      setShowModal(false);
    }
  }, [onRemoveImage]);

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  };

  const hasProfileImage = !!(previewImage || userData.profileImage);

  const gradientBorderStyle = {
    background: `conic-gradient(
      from 0deg,
      #feda75,
      #fa7e1e,
      #d62976,
      #962fbf,
      #4f5bd5,
      #feda75
    )`,
    padding: "3px",
    borderRadius: "50%",
    display: "inline-block",
  };

  return (
    <div className="mb-6 sm:mb-8">
      <div className="p-4 sm:p-6 md:p-8">
        <div className="flex flex-row sm:flex-row gap-4 sm:gap-6 md:gap-8 items-center sm:items-start edit-profile-profile-section">
          {/* Profile Picture - Left Side */}
          <div className="flex-shrink-0 self-center sm:self-start">
            <div onClick={handlePlusClick} className="relative group cursor-pointer">
              {/* Gradient Border Wrapper */}
              <div style={gradientBorderStyle}>
                <div className="w-28 h-28 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-36 lg:h-36 rounded-full overflow-hidden bg-white p-[3px] hover:shadow-lg transition-all duration-300">
                  {previewImage || userData.profileImage ? (
                    typeof previewImage === "string" ? (
                      <img
                        src={previewImage}
                        alt="Profile preview"
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <ResponsiveProfileImage
                        imageUrls={previewImage || userData.profileImage}
                        alt={userData.name}
                        className="w-full h-full object-cover rounded-full"
                      />
                    )
                  ) : (
                    <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden">
                      {userData.name ? (
                        <span
                          className="text-slate-600 font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-6xl"
                          style={{
                            fontFamily:
                              'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                          }}
                        >
                          {getInitials(userData.name)}
                        </span>
                      ) : (
                        <svg
                          className="w-full h-full text-slate-400"
                          viewBox="0 0 100 100"
                          fill="currentColor"
                        >
                          <circle cx="50" cy="35" r="18" />
                          <path d="M50 58 C 25 58, 10 65, 10 100 L 90 100 C 90 65, 75 58, 50 58 Z" />
                        </svg>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Plus Icon Button - Bottom Right */}
              <button
                type="button"
                className="
                  absolute 
                  bottom-2 
                  right-2
                  w-7 h-7
                  sm:w-8 sm:h-8
                  bg-black
                  rounded-full
                  border-[3px] border-white
                  flex items-center justify-center
                  transition-all duration-200
                  z-10
                "
              >
                {isUploadingImage || isRemovingImage ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M12 5v14m7-7H5"
                    />
                  </svg>
                )}
              </button>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />
            </div>
          </div>

          {/* Profile Info - Right Side */}
          <div className="flex-1 min-w-0 w-full text-center sm:text-left">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-black truncate mb-1">
              {userData.name}
            </h2>

            <div className="flex items-center justify-center sm:justify-start gap-1 mb-1">
              <p className="text-base font-normal text-black truncate">
                {userData.username}
              </p>
            </div>

            {userData.bio && (
              <p className="text-sm font-normal text-black leading-relaxed mb-1 line-clamp-3">
                {userData.bio}
              </p>
            )}

            {userData.website && (
              <div className="flex items-center justify-center sm:justify-start gap-1 text-sm text-black">
                <Link className="w-4 h-4 flex-shrink-0" />
                <a
                  href={
                    userData.website.startsWith("http")
                      ? userData.website
                      : `https://${userData.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline truncate"
                >
                  {userData.website.replace(/^https?:\/\//, "")}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Image Modal */}
      {showModal && (
        <ProfileImageModal
          previewImage={previewImage}
          userData={userData}
          onClose={() => setShowModal(false)}
          onUploadClick={handleUploadClick}
          onRemoveClick={handleRemoveClick}
          hasProfileImage={hasProfileImage}
          isUploading={isUploadingImage}
          isRemoving={isRemovingImage}
        />
      )}
    </div>
  );
}
